import React from "react";
import * as XLSX from "xlsx";
import chroma from "chroma-js";
import { Order, SortOrder } from "../constants/enums";

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}



export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === SortOrder.DESC
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}


export function convertTimeToISOString(timeStr:string) {
  // Today's date
  const today = new Date();
  const [hours, minutes, seconds] = timeStr.split(":");

  // Set hours, minutes, and seconds based on the provided time
  today.setHours(+hours);
  today.setMinutes(+minutes);
  today.setSeconds(+seconds);

  // Return the ISO string in UTC format
  return today.toISOString();
}

export const handleErrors = (error: any) => {
  if (error.response) {
    // Request made and server responded
    return error.response.data;
  } else if (error.request) {
    // The request was made but no response was received
    return error.request;
  } else {
    // Something happened in setting up the request that triggered an Error
    return error.message;
  }
};
/**
 * Convert string hours to it's equivalent in numbers
 *
 * @param hours hours: string
 * @param format format: string
 *
 * @returns number
 */
export const hoursStringToNumber = (hours: string, format: string): number => {
  const time = hours.split(":")[0];

  return format === "PM"
    ? parseInt(time) + 12 === 24
      ? 12
      : parseInt(time) + 12
    : parseInt(time) === 12
    ? 0
    : parseInt(time);
};

/**
 * Compare giver hour in number to current locale hour
 *
 * @param hour hour: number
 *
 * @returns number
 */
export const compareHoursToCurrent = (hour: number): number => {
  const d = new Date();
  let currentHour = d.getHours();

  return hour - currentHour;
};

/**
 * Capitalize first letter of a given string
 *
 * @param string string: string
 *
 * @returns string
 */
export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function stableSort(array: readonly any[], comparator: any) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function randomStr(length: number) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const formatAMPM = (isoDate: string) => {
  const date = new Date(isoDate);
  let hours: string | number = date.getHours();
  let minutes: string | number = date.getMinutes();
  const ampm = hours >= 12 ? "pm " : "am";

  hours %= 12;
  hours = hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  const strTime = `${hours}:${minutes} ${ampm}`;

  return strTime;
};

export function getDayNumber(dateStr: string, locale: string) {
  const date = new Date(dateStr);
  return date.getDate();
}

export function getDateFormatted(date: Date) {
  const today = date ? new Date(date) : new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();

  return yyyy + "- " + mm + "- " + dd;
}

export function capitalizeWords(string: string) {
  return string.replace(/(?:^|\s)\S/g, function (a) {
    return a.toUpperCase();
  });
}

export const mergeListOfStringsByDash = (list: string[] | null) => {
  if (list !== null && list.length)
    return list.reduce((prev, current) => `${prev} - ${current}`);

  return "";
};

export const mergeListOfStrings = (
  list: (string | null)[],
  delimiter: string
) => {
  if (list !== null && list.length)
    return list.reduce((prev, current) => `${prev} ${delimiter} ${current}`);

  return "";
};

export const makeFakePhoneNumbers = (index: number, length: number) => {
  var result = `01${index}`;
  var characters = "0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const getDomainName = () => {
  if (window.location.hostname === "localhost") return "itzone";
  return "itzone";
};

export const createArray = (size: number) =>
  Array.from({ length: size }, (_, i) => i);

export function convertToCamelCase(text: string) {
  return text.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase();
  });
}

export function extractSelectedCheckboxes(keyName: string, data: any) {
  const selected: string[] = [];

  for (let key of Object.getOwnPropertyNames(data)) {
    if (key.includes(keyName)) {
      if (data[key] === true) {
        selected.push(key.replace(keyName, ""));
      }
    }
  }

  return selected;
}

export function validatePhone(phone: string) {
  var re = /^[0][1-9]\d{9}$|^[1-9]\d{9}$/;
  return re.test(phone);
}

export function convertToSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
}

export function camelCaseToSpaces(text: string): string {
  const result: string = text.replace(/([A-Z])/g, "$1");
  return result.charAt(0).toUpperCase() + result.slice(1);
}

/**
  The pxToRem() function helps you to convert a px unit into a rem unit,
 */
export function pxToRem(number: number, baseNumber: number = 16): string {
  return `${number / baseNumber}rem`;
}

export function shuffleArray(array: any[]) {
  let currentIndex = array.length;

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

/**
  The hexToRgb() function helps you to change the hex color code to rgb
  using chroma-js library.
 */

/**
  The linearGradient() function helps you to create a linear gradient color background
 */

export function linearGradient(
  color: string,
  colorState: string,
  angle: number = 195
): string {
  return `linear-gradient(${angle}deg, ${color}, ${colorState})`;
}

export function excerpt(str: string, maxLength: number): string {
  if (str.length > maxLength) return str.substring(0, maxLength) + "...";

  return str;
}
/**
  The rgba() function helps you to create a rgba color code, it uses the hexToRgb() function
  to convert the hex code into rgb for using it inside the rgba color format.
 */

/**
  The gradientChartLine() function helps you to create a gradient color for the chart line
 */

/**
  The boxShadow() function helps you to create a box shadow for an element
 */

export function getDayTime(): string {
  const today: Date = new Date();
  const curHr: number = today.getHours();

  if (curHr < 12) {
    return "morning";
  } else if (curHr < 18) {
    return "afternoon";
  } else {
    return "evening";
  }
}

export const removeUndefined = (obj: any) => {
  Object.keys(obj).forEach((key) =>
    obj[key] === undefined ? delete obj[key] : {}
  );
  return obj;
};
/**
 * Reformats a number into a currency string
 *
 * @param number number: number
 *
 * @returns string
 */
export default function getCurrencyFormat(number: number): string {
  const formatter = Intl.NumberFormat("en", {
    maximumSignificantDigits: 3,
    style: "currency",
    currency: "USD",
  });
  return formatter.format(number);
}

export const handleError = (error: any) => {
  if (typeof error === "string") return error;

  if (error.response) {
    const data = error.response.data;

    // Check for specific `errors` structure and extract messages
    if (data.errors && typeof data.errors === "object") {
      const errorMessages = Object.entries(data.errors)
        .flatMap(([field, messages]) => 
          Array.isArray(messages) ? messages.map(msg => `${field}: ${msg}`) : []
        )
        .join("\n");
      return errorMessages || "An error occurred.";
    }

    // Fallback to other message fields
    return data.message || data.error || data.title || data.englishMessage || data;
  } else if (error.request) {
    // The request was made but no response was received
    return "Something went wrong";
  } else {
    // Something happened in setting up the request that triggered an Error
    return error.message;
  }
};


type RGB = {
  r: number;
  g: number;
  b: number;
};

const hexToRgb = (hex: string): RGB => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const rgbToHex = ({ r, g, b }: RGB): string => {
  const toHex = (x: number) => x.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const adjustColor = (color: RGB, factor: number): RGB => {
  return {
    r: Math.min(255, Math.max(0, color.r + factor)),
    g: Math.min(255, Math.max(0, color.g + factor)),
    b: Math.min(255, Math.max(0, color.b + factor)),
  };
};

export const generateColorShades = (baseColor: string): string[] => {
  const baseRgb = hexToRgb(baseColor);
  const factor = 40; // Change this to adjust the step size

  return [
    rgbToHex(adjustColor(baseRgb, 2 * factor)), // Lighter color
    rgbToHex(adjustColor(baseRgb, factor)), // Light color
    baseColor, // Base color
    rgbToHex(adjustColor(baseRgb, -factor)), // Dark color
    rgbToHex(adjustColor(baseRgb, -2 * factor)), // Darker color
  ];
};


export function isValidEmail(email:string) {
  const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return emailRegex.test(email);
}

export const generateColorGradient = (
  baseColor: string = "#0000FF",
  num: number = 10,
  brighten: number = 3,
  darken: number = 3
): string[] => {
  const scale = chroma
    .scale([
      chroma(baseColor).brighten(brighten),
      baseColor,
      chroma(baseColor).darken(darken),
    ])
    .mode("lab")
    .colors(num);
  return scale;
};


export const validatePassword = (password: string): boolean => {

  
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

  if (!passwordRegex.test(password)) {
    return false
  }

  return true
};
